import { AUTH_CONFIG, DEFAULT_SESSION_CONFIG } from './auth-config-token';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject, interval } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

export interface SessionTimerState {
    remainingMinutes: number;
    isExpired: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SessionTimerService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly config = inject(AUTH_CONFIG);

    private readonly sessionConfig = {
        ...DEFAULT_SESSION_CONFIG,
        ...(this.config.sessionConfig || {})
    };

    private readonly _sessionState = new BehaviorSubject<SessionTimerState>({
        remainingMinutes: this.sessionConfig.expirationMinutes,
        isExpired: false,
    });

    public readonly sessionState$ = this._sessionState.asObservable();

    private lastResetTime = Date.now();
    private warningsEmitted = new Set<number>();
    private timerStarted = false;

    constructor() {
        // Don't auto-start the timer
    }

    public startTimer(): void {
        if (this.timerStarted) {
            return;
        }

        this.timerStarted = true;
        this.resetTimer();

        // Update every second for accurate countdown
        interval(1000)
            .pipe(
                takeUntil(this.destroy$),
                tap(() => this.updateSessionState())
            )
            .subscribe();
    }

    public stopTimer(): void {
        this.timerStarted = false;
        this.warningsEmitted.clear();
        this._sessionState.next({
            remainingMinutes: this.sessionConfig.expirationMinutes,
            isExpired: false,
        });
    }

    public isSessionExpired(): boolean {
        return this._sessionState.value.isExpired;
    }

    private updateSessionState(): void {
        if (!this.timerStarted) {
            return;
        }

        const elapsedMinutes = (Date.now() - this.lastResetTime) / (1000 * 60);
        const remainingMinutes = Math.max(0, this.sessionConfig.expirationMinutes - elapsedMinutes);

        const isExpired = remainingMinutes === 0;

        this._sessionState.next({
            remainingMinutes,
            isExpired,
        });
    }

    public resetTimer(): void {
        this.lastResetTime = Date.now();
        this.warningsEmitted.clear();

        this._sessionState.next({
            remainingMinutes: this.sessionConfig.expirationMinutes,
            isExpired: false,
        });
    }

    public getRemainingSeconds(): number {
        const elapsedMs = Date.now() - this.lastResetTime;
        const totalMs = this.sessionConfig.expirationMinutes * 60 * 1000;
        const remainingMs = Math.max(0, totalMs - elapsedMs);
        return Math.floor(remainingMs / 1000);
    }

    public getFormattedRemainingTime(): string {
        const totalSeconds = this.getRemainingSeconds();
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    public extendSession(): void {
        // This could be called after successful re-authentication
        this.resetTimer();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
